import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

/**
 * Deletes a company logo.
 *
 * @param {string} companyId - The ID of the company for which to delete the logo.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/remove_company_logo_company__id__logo_delete
 */
// fallow-ignore-next-line unused-export
export const deleteCompanyLogo = async (companyId: UUID) => {
  try {
    return await vestigasApi
      .delete(ENDPOINT.COMPANY.LOGO.DELETE(companyId), {
        validate: { response: zodSuccessResponseSchema },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error deleting company logo',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for deleting a company logo.
 *
 * This hook provides functionality to:
 * - Delete the logo image for a company
 * - Automatically invalidate the company query cache on completion
 *
 * @returns {UseMutationResult} A mutation object containing the delete function and status
 *
 * @example
 * const mutation = useMutationCompanyLogoDelete(companyId);
 * mutation.mutate();
 */
export const useMutationCompanyLogoDelete = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, UUID>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, UUID>({
    mutationFn: async (companyId: UUID) => deleteCompanyLogo(companyId),
    onSettled(_, __, companyId) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAdmin(companyId),
      });
    },
    ...options,
  });
};
